/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {
    getSearchParams,
    navigate,
    PDFElementParams,
    PdfFont,
    ResponseData,
    showDialog,
    showSnackbar
} from '@dvrd/dvr-controls';
import {AppContext} from "../../misc/appContext";
import Account from "../../../models/accountModel";
import PDFTemplate from "../../../models/pdfTemplateModel";
import {sendGaEvent, sendGaPageView, trackPlatformAction} from "../../../util/analytics";
import {createDefaultValues, prepareForSubmit, templateToItems} from "../../../util/pdfUtil";
import {createErrorMessage} from "../../../util/utils";
import {onInvoicePreview, onSetCrumbs} from "../../../util/events";
import PdfTemplateEditor from "./pdfTemplateEditor";
import {useIsFirstRender} from "../../../util/componentUtil";
import {hasAdminLogin} from "../../../util/adminUtil";
import {copyPDFTemplate} from "../../../util/requestSender";
import {ActivateHandle} from "../../../util/interfaces";
import AccountSelectController from "../../accountSelect/accountSelectController";
import PageChangePermission from "../../misc/pageChangePermission";


export default function PdfTemplateController() {
    const context = useContext(AppContext);
    const account = context.accountContext.account ?? new Account({});
    const [loading, setLoading] = useState(false);
    const [fromRegister, setFromRegister] = useState(false);
    const [template, setTemplate] = useState(account.template ?? new PDFTemplate({
        account_id: account.id,
        main_font: PdfFont.LATO
    }));
    const hasAccounts = context.accountContext.accounts.length > 1;
    const [version, setVersion] = useState(0);
    const isFirstRender = useIsFirstRender();
    const importLayoutRef = useRef<ActivateHandle>(null);

    function onChangeFont(font: PdfFont) {
        const tpl = template.clone();
        tpl.mainFont = font;
        setTemplate(tpl);
    }

    function onClickProceed(items: PDFElementParams<any, any>[]) {
        onSubmit(false)(items, () => {
            navigate('/facturen');
        });
    }

    function onPreview(items: PDFElementParams<any, any>[]) {
        sendGaEvent('Click', 'PDF Template', 'Voorbeeld');
        setLoading(true);
        const config = prepareForSubmit(items);
        template.preview(config, (data: ResponseData, response: Response) => {
            setLoading(false);
            if (data.status === 'success') {
                response.blob().then((blob: Blob) => {
                    onInvoicePreview(URL.createObjectURL(blob));
                });
            } else
                showDialog(createErrorMessage(data.message ?? 'Het aanmaken van een voorbeeld factuur is niet gelukt.'),
                    'Voorbeeld mislukt');
        })
    }

    function onSubmit(sendEvent: boolean = true) {
        return function(items: PDFElementParams<any, any>[], callback?: VoidFunction) {
            if (sendEvent) sendGaEvent('Click', 'PDF Template', 'Opslaan');
            setLoading(true);
            const config = prepareForSubmit(items);
            template.update(config, (template: PDFTemplate, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    trackPlatformAction('invoices.layout', 'save_layout');
                    showSnackbar('De PDF template is opgeslagen');
                    account.template = template;
                    context.onChangeContext({account}, callback);
                } else showDialog(createErrorMessage(data.message ?? 'Het opslaan van de PDF template is niet gelukt'),
                    'Opslaan mislukt');
            })
        }
    }

    function onClickImport() {
        importLayoutRef.current?.activate();
    }

    function onImportFromAccount(accountId: string) {
        setLoading(true);
        copyPDFTemplate({
            data: {
                account_id: account.id, from_account_id: accountId,
            }, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    account.template = new PDFTemplate(data.pdf_template);
                    context.onChangeContext({account});
                    showSnackbar('De layout is gekopieerd');
                    importLayoutRef.current?.close?.();
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het kopiëren van de layout is niet gelukt.'),
                        'Kopiëren mislukt')
            }
        })
    }

    function saveAccountSetupProgress(account: Account) {
        if (hasAdminLogin()) return;
        if (account.setupProgress.pdf_layout) return;
        account.setupProgress.pdf_layout = true;
        account.update(undefined, undefined, undefined,
            (account: Account, success: boolean) => {
                if (success) context.onChangeContext({account});
            })
    }

    useEffect(() => {
        sendGaPageView('/factuur-layout');
        onSetCrumbs([{label: 'Factuur Layout', route: '/factuur-layout'}]);
        const params = getSearchParams();
        if (params.get('registratie') === '1')
            setFromRegister(true);
    }, []);

    useEffect(() => {
        const account = context.accountContext.account;
        if (account) {
            const template = account.template?.clone() ?? new PDFTemplate({
                account_id: account.id,
                main_font: PdfFont.LATO
            })
            setTemplate(template);
            if (!isFirstRender)
                setVersion(version + 1);
            saveAccountSetupProgress(account)
        }
    }, [context.accountContext.account, context.accountContext.account?.template]);

    return (
        <>
            <PdfTemplateEditor onPreview={onPreview} onSubmit={onSubmit()} onClickImport={onClickImport}
                               onClickProceed={fromRegister ? onClickProceed : null}
                               onChangeFont={onChangeFont} elements={templateToItems(template)}
                               resetElements={templateToItems(template)}
                               loading={loading} values={createDefaultValues(account)} mainFont={template.mainFont}
                               version={version} hasAccounts={hasAccounts}/>
            <AccountSelectController ref={importLayoutRef} onImport={onImportFromAccount} loading={loading}
                                     title='Template ophalen'>
                <p className='import-text'>
                    Factuur template uit een ander account ophalen.
                    <br/><br/>
                    <b>Let op: </b>huidige template wordt overschreven!
                </p>
            </AccountSelectController>
            <PageChangePermission changed denyText='Nee, niet verlaten' allowText='Ja, pagina verlaten' allowSecondary>
                Eventuele wijzigingen gaan verloren als je de pagina verlaat zonder de wijzigingen op te slaan. Weet je
                zeker dat je de pagina wilt verlaten?
            </PageChangePermission>
        </>
    )
}